import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useEffect, useRef, useState} from 'react'
import {
  ArrowLeftIcon,
  CopilotIcon,
  PeopleIcon,
  BookIcon,
  DeviceMobileIcon,
  IterationsIcon,
} from '@primer/octicons-react'
import {
  ThemeProvider,
  Hero,
  Grid,
  Image,
  LogoSuite,
  AnchorNav,
  SectionIntro,
  FAQ,
  FAQGroup,
  Bento,
  Link,
  Box,
  Text,
  River,
  Heading,
  Label,
  RiverBreakout,
  Timeline,
  Button,
  UnorderedList,
  Stack,
  Card,
  AnimationProvider,
} from '@primer/react-brand'

export function CopilotIndex() {
  const {copilotSignupPath} = useRoutePayload<{copilotSignupPath: string}>()
  const {copilotForBusinessSignupPath} = useRoutePayload<{copilotForBusinessSignupPath: string}>()
  const {copilotHeroSignupPath} = useRoutePayload<{copilotHeroSignupPath: string}>()

  // Video files
  const {heroVideoLg} = useRoutePayload<{heroVideoLg: string}>()
  const {heroVideoSm} = useRoutePayload<{heroVideoSm: string}>()

  // Video player
  const videoLgRef = useRef<HTMLVideoElement>(null)
  const videoSmRef = useRef<HTMLVideoElement>(null)
  const [videoIsPlaying, setVideoIsPlaying] = useState(true)

  const handleVideoReplay = () => {
    if (videoLgRef.current && videoSmRef.current) {
      videoLgRef.current.currentTime = 0
      videoSmRef.current.currentTime = 0
      setVideoIsPlaying(true)
      videoLgRef.current.play()
      videoSmRef.current.play()
    }
  }

  // Pricing deep link
  //
  // This is an alternative to using `href="#pricing"` on the Hero "Compare Plans" button
  // It seems deep linking currently doesn't work reliably, so we're using `scrollTo()` instead

  const ctaPricingRef = useRef<HTMLButtonElement>(null)
  const sectionPricingRef = useRef<HTMLElement>(null)

  const handlePricingClick = (event: Event) => {
    event.preventDefault()

    if (sectionPricingRef.current) {
      window.scrollTo({
        top: sectionPricingRef.current.offsetTop,
      })
    }
  }

  useEffect(() => {
    const currentCtaPricingRef = ctaPricingRef.current

    if (currentCtaPricingRef) {
      currentCtaPricingRef.addEventListener('click', handlePricingClick)
    }

    return () => {
      if (currentCtaPricingRef) {
        currentCtaPricingRef.removeEventListener('click', handlePricingClick)
      }
    }
  }, [])

  return (
    <>
      <ThemeProvider colorMode="dark" className="Copilot">
        <section id="hero" className="Section Section--hero">
          <Grid>
            <Grid.Column span={12}>
              <Hero data-hpc align="center" className="Hero">
                <Hero.Label size="large" leadingVisual={<CopilotIcon />}>
                  GitHub Copilot
                </Hero.Label>
                <Hero.Heading size="1" weight="bold" className="Hero-heading">
                  The world’s most widely adopted AI developer tool.
                </Hero.Heading>
                <div className="Hero-ctaButtons">
                  <Hero.PrimaryAction href={copilotHeroSignupPath} className="Button--heroCta Button--narrowGap">
                    Buy now
                  </Hero.PrimaryAction>
                  <Hero.SecondaryAction
                    href=""
                    hasArrow={false}
                    className="Button--heroCta Button--onColoredBg"
                    ref={ctaPricingRef}
                  >
                    Compare Plans
                  </Hero.SecondaryAction>
                </div>
              </Hero>

              <Box className="Hero-visual">
                <Box className="Hero-videoContainer">
                  <video
                    autoPlay={true}
                    playsInline={true}
                    muted={true}
                    className="Hero-video d-none d-md-block hide-reduced-motion"
                    width="1248"
                    height="735"
                    ref={videoLgRef}
                    onEnded={() => setVideoIsPlaying(false)}
                  >
                    <source src={heroVideoLg} type="video/mp4; codecs=avc1.4d002a" />
                  </video>

                  <video
                    autoPlay={true}
                    playsInline={true}
                    muted={true}
                    className="Hero-video d-md-none hide-reduced-motion"
                    width="539.5"
                    height="682"
                    ref={videoSmRef}
                    onEnded={() => setVideoIsPlaying(false)}
                  >
                    <source src={heroVideoSm} type="video/mp4; codecs=avc1.4d002a" />
                  </video>

                  <Image
                    className="Hero-videoImage hide-no-pref-motion"
                    src="/images/modules/site/copilot/react/hero.jpg"
                    alt="Editor with GitHub Copilot Chat"
                    width="1248"
                  />
                </Box>

                <Box paddingBlockStart={12} className="Hero-videoPlayer">
                  <Button
                    leadingVisual={IterationsIcon}
                    variant="subtle"
                    hasArrow={false}
                    className={`Hero-videoPlayerButton ${videoIsPlaying ? 'is-playing' : ''}`}
                    onClick={handleVideoReplay}
                  >
                    Replay
                  </Button>
                </Box>

                <Box className="Hero-head">
                  <div className="Hero-headSize">
                    <div className="Hero-headBlink">
                      <Image
                        className="Hero-headImage"
                        src="/images/modules/site/copilot/react/hero-head.png"
                        width="600"
                        height="179"
                        alt=""
                      />
                    </div>
                  </div>
                </Box>
              </Box>

              <LogoSuite hasDivider={false}>
                <LogoSuite.Heading visuallyHidden>GitHub Copilot is used by</LogoSuite.Heading>
                <LogoSuite.Logobar marquee marqueeSpeed="slow">
                  <Image
                    src="/images/modules/site/copilot/react/logos/coca-cola.svg"
                    alt="Coca Cola's logo"
                    height={40}
                  />
                  <Image
                    src="/images/modules/site/copilot/react/logos/coyote-logistics.svg"
                    alt="Coyote Logistics's logo"
                    height={40}
                  />
                  <Image
                    src="/images/modules/site/copilot/react/logos/duolingo.svg"
                    alt="Duolingo's logo"
                    height={40}
                  />
                  <Image src="/images/modules/site/copilot/react/logos/stripe.svg" alt="Stripe's logo" height={44} />
                  <Image src="/images/modules/site/copilot/react/logos/shopify.svg" alt="Shopify's logo" height={40} />
                  <Image
                    src="/images/modules/site/copilot/react/logos/mercado-libre.svg"
                    alt="Mercado Libre's logo"
                    height={40}
                  />
                  <Image
                    src="/images/modules/site/copilot/react/logos/mercedes.svg"
                    alt="Mercedes Benz's logo"
                    height={48}
                  />
                  <Image
                    src="/images/modules/site/copilot/react/logos/fidelity.svg"
                    alt="Fidelity's logo"
                    height={36}
                  />
                  <Image src="/images/modules/site/copilot/react/logos/dell.svg" alt="Dell's logo" height={60} />
                  <Image
                    src="/images/modules/site/copilot/react/logos/lemonade.svg"
                    alt="Lemonade's logo"
                    height={36}
                  />
                  <Image
                    src="/images/modules/site/copilot/react/logos/sas.svg"
                    alt="Scandinavian Airlines's logo"
                    height={36}
                  />
                  <Image src="/images/modules/site/copilot/react/logos/itau.svg" alt="Itau's logo" height={50} />
                  <Image src="/images/modules/site/copilot/react/logos/hover.svg" alt="Hover's logo" height={36} />
                </LogoSuite.Logobar>
              </LogoSuite>
            </Grid.Column>
          </Grid>

          <Box style={{height: 0}}>
            <AnchorNav hideUntilSticky>
              <AnchorNav.Link href="#enterprise">Enterprise-ready</AnchorNav.Link>
              <AnchorNav.Link href="#features">Features</AnchorNav.Link>
              <AnchorNav.Link href="#pricing">Pricing</AnchorNav.Link>
              <AnchorNav.Link href="#faq">FAQs</AnchorNav.Link>
              <AnchorNav.Action href={copilotHeroSignupPath}>Buy now</AnchorNav.Action>
            </AnchorNav>
          </Box>
          <div className="Hero-cover" />
        </section>

        <section id="enterprise" className="Section Section--level-1">
          <Grid className="Section-container--centerUntilMedium Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="SectionIntro">
                <SectionIntro.Label size="large" className="Label--section">
                  Enterprise-ready
                </SectionIntro.Label>
                <SectionIntro.Heading size="1" weight="bold">
                  The competitive advantage developers ask for by name.
                </SectionIntro.Heading>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <Bento className="Bento Bento--inset">
                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 7}}
                  rowSpan={{xsmall: 4, small: 3, medium: 4, large: 5, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}}>
                    <Bento.Heading as="h3" size="3" weight="semibold">
                      Proven to increase developer productivity and accelerate the pace of software development.
                    </Bento.Heading>
                    <Link
                      href="https://github.blog/2022-09-07-research-quantifying-github-copilots-impact-on-developer-productivity-and-happiness/"
                      size="large"
                      variant="default"
                    >
                      Read the research
                    </Link>
                  </Bento.Content>
                  <Bento.Visual>
                    <Image src="/images/modules/site/copilot/react/enterprise-1.jpg" alt="" width="724" height="540" />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 5}}
                  rowSpan={{xsmall: 3, small: 3, medium: 4, large: 5, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    verticalAlign="end"
                    className="Bento-lastChildNoMarginBottom"
                  >
                    <Bento.Heading as="h3" size="display" className="sansSerifAlt">
                      55%
                    </Bento.Heading>
                    <Text as="p" size="400" weight="medium" variant="muted" style={{marginBottom: '0'}}>
                      Faster coding
                    </Text>
                  </Bento.Content>
                  <Bento.Visual position="0% 0%">
                    <Image src="/images/modules/site/copilot/react/enterprise-2.webp" alt="" width="492" height="540" />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 5}}
                  rowSpan={{xsmall: 3, small: 3, medium: 4, large: 4, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    verticalAlign="end"
                    className="Bento-lastChildNoMarginBottom"
                  >
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Designed by leaders in AI so you can build with confidence.
                    </Bento.Heading>
                  </Bento.Content>
                  <Bento.Visual position="0% 0%">
                    <Image src="/images/modules/site/copilot/react/enterprise-3.webp" alt="" width="492" height="540" />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 7}}
                  rowSpan={{xsmall: 3, small: 3, medium: 4, large: 4, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}}>
                    <Bento.Heading as="h3" size="3" weight="semibold">
                      Committed to your privacy, security, and trust.
                    </Bento.Heading>
                    <Link href="https://resources.github.com/copilot-trust-center/" size="large" variant="default">
                      Visit the Copilot Trust Center
                    </Link>
                  </Bento.Content>
                  <Bento.Visual>
                    <Image src="/images/modules/site/copilot/react/enterprise-4.jpg" alt="" width="724" height="540" />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={12}
                  rowSpan={{xsmall: 5, small: 5, medium: 4, large: 4, xlarge: 5}}
                  flow={{xsmall: 'row', medium: 'column'}}
                  className="Bento-item"
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}}>
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Duolingo empowers its engineers to be force multipliers for expertise with GitHub Copilot,
                      Codespaces.
                    </Bento.Heading>
                    <Link href="https://github.com/customer-stories/duolingo" size="large" variant="default">
                      Read customer story
                    </Link>
                  </Bento.Content>
                  <Bento.Visual padding="normal">
                    <Image
                      src="/images/modules/site/copilot/react/enterprise-5.jpg"
                      alt="Mobile device using Duolingo app"
                      width="570"
                      height="532"
                    />
                  </Bento.Visual>
                </Bento.Item>
              </Bento>
            </Grid.Column>
          </Grid>
        </section>

        <section id="productivity" className="Section Section--compact Section--level-gradient Section--illu">
          <Grid
            className="Section-container--centerUntilMedium Grid--noRowGap Section--illu-content"
            style={{paddingBottom: '32px'}}
          >
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="SectionIntro">
                <SectionIntro.Heading size="3">
                  The industry <br className="breakWhenNarrow" /> standard.
                </SectionIntro.Heading>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <AnimationProvider runOnce={true}>
                <Stack
                  direction={{narrow: 'vertical', regular: 'horizontal'}}
                  gap={32}
                  padding="none"
                  className="Productivity"
                >
                  <Box
                    padding={32}
                    className="Productivity-item has-BlurredBg has-GradientBorder"
                    animate="slide-in-up"
                  >
                    <Heading
                      as="h3"
                      weight="medium"
                      className="sansSerifAlt"
                      style={{fontSize: '56px', letterSpacing: '-1px'}}
                    >
                      37,000+
                    </Heading>
                    <Text as="p" weight="medium" variant="muted" style={{fontSize: '24px', letterSpacing: '-0.48px'}}>
                      Businesses have adopted GitHub Copilot
                    </Text>
                  </Box>

                  <Box
                    padding={32}
                    className="Productivity-item has-BlurredBg has-GradientBorder"
                    animate="slide-in-up"
                  >
                    <Heading
                      as="h3"
                      weight="medium"
                      className="sansSerifAlt"
                      style={{fontSize: '56px', letterSpacing: '-1px'}}
                    >
                      1 in 3
                    </Heading>
                    <Text as="p" weight="medium" variant="muted" style={{fontSize: '24px', letterSpacing: '-0.48px'}}>
                      Fortune 500 companies use GitHub Copilot
                    </Text>
                    <Text size="200" variant="muted" style={{marginTop: '28px'}}>
                      Available for business since Dec 2022
                    </Text>
                  </Box>

                  <Box
                    padding={32}
                    className="Productivity-item has-BlurredBg has-GradientBorder"
                    animate="slide-in-up"
                  >
                    <Heading
                      as="h3"
                      weight="medium"
                      className="sansSerifAlt"
                      style={{fontSize: '56px', letterSpacing: '-1px'}}
                    >
                      55%
                    </Heading>
                    <Text as="p" weight="medium" variant="muted" style={{fontSize: '24px', letterSpacing: '-0.48px'}}>
                      Developer preference for GitHub Copilot
                    </Text>
                    <Text size="200" variant="muted" style={{marginTop: '28px'}}>
                      Stack Overflow 2023 Survey
                    </Text>
                  </Box>
                </Stack>
              </AnimationProvider>
            </Grid.Column>
          </Grid>

          <Image
            src="/images/modules/site/copilot/react/productivity-bg-2.webp"
            alt=""
            width="600"
            height="350"
            className="Productivity-bg--kite2"
          />
          <Image
            src="/images/modules/site/copilot/react/productivity-bg-1.webp"
            alt=""
            width="706"
            height="338"
            className="Productivity-bg--kite1"
          />
          <Image
            src="/images/modules/site/copilot/react/productivity-bg-head.png"
            alt=""
            width="234"
            height="204"
            className="Section--illu-img Productivity-bg--mascot"
          />
        </section>

        <section id="features" className="Section Section--noBottomPadding">
          <Grid className="Section-container--centerUntilMedium Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="SectionIntro">
                <SectionIntro.Label size="large" className="Label--section">
                  Features
                </SectionIntro.Label>
                <SectionIntro.Heading as="h2" size="1" weight="bold">
                  The AI coding assistant <br className="breakWhenWide" /> elevating developer workflows.
                </SectionIntro.Heading>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <RiverBreakout style={{paddingTop: '0'}}>
                <RiverBreakout.A11yHeading>Accelerate workflows</RiverBreakout.A11yHeading>
                <RiverBreakout.Visual>
                  <Image
                    src="/images/modules/site/copilot/react/features-breakout.webp"
                    alt="Screenshot showing GitHub Copilot"
                    width="1248"
                    height="647"
                    style={{display: 'block'}}
                  />
                </RiverBreakout.Visual>
                <RiverBreakout.Content
                  trailingComponent={() => (
                    <Timeline className="Timeline">
                      <Timeline.Item>
                        <em>Improve code quality and security.</em> Developers feel{' '}
                        <a
                          className="Link--inline"
                          href="https://github.blog/2023-10-10-research-quantifying-github-copilots-impact-on-code-quality/"
                        >
                          more confident in their code quality
                        </a>{' '}
                        when authoring code with Copilot. And with the built-in{' '}
                        <a
                          className="Link--inline"
                          href="https://github.blog/2023-02-14-github-copilot-now-has-a-better-ai-model-and-new-capabilities/#filtering-out-security-vulnerabilities-with-a-new-ai-system"
                        >
                          vulnerability prevention system
                        </a>
                        , insecure coding patterns get blocked in real-time.
                      </Timeline.Item>
                      <Timeline.Item>
                        <em>Enable greater collaboration.</em> Copilot indexes and understands your repositories so
                        developers can quickly ramp into new codebases, improve legacy code, and build off each other’s
                        work.
                      </Timeline.Item>
                    </Timeline>
                  )}
                >
                  <Text>
                    <em>Start a conversation about your codebase.</em> Whether you’re hunting down a bug or designing a
                    new feature - when you’re stuck, ask <br className="breakWhenWide" />
                    GitHub Copilot.
                  </Text>
                </RiverBreakout.Content>
              </RiverBreakout>

              <AnimationProvider runOnce={true} autoStaggerChildren={false}>
                <River imageTextRatio="60:40" animate="slide-in-up">
                  <River.Visual>
                    <Image
                      src="/images/modules/site/copilot/react/features-river-1.webp"
                      alt="Editor with GitHub Copilot Chat"
                      width="708"
                      height="472"
                    />
                  </River.Visual>
                  <River.Content>
                    <Heading as="h3">Get AI-based suggestions in real-time.</Heading>
                    <Text as="p" variant="muted" className="River-text">
                      GitHub Copilot suggests code completions as developers type and turns natural language prompts
                      into coding suggestions based on the project&apos;s context and style conventions.
                    </Text>
                  </River.Content>
                </River>

                <River imageTextRatio="60:40" animate="slide-in-up">
                  <River.Visual>
                    <Image
                      src="/images/modules/site/copilot/react/features-river-2.webp"
                      alt="GitHub Copilot showing different credential sources"
                      width="708"
                      height="472"
                    />
                  </River.Visual>
                  <River.Content>
                    <Heading as="h3">
                      <Label className="Label--inRiver">Copilot Enterprise</Label>
                      Docs that feel tailored for you.
                    </Heading>
                    <Text as="p" variant="muted" className="River-text">
                      Spend less time searching and more time learning, by getting personalized answers that are
                      grounded in your organization’s documentation, with inline citations. Load content → Ask question
                      → Profit.
                    </Text>
                  </River.Content>
                </River>

                <River imageTextRatio="60:40" animate="slide-in-up" style={{paddingBottom: '0'}}>
                  <River.Visual>
                    <Image
                      src="/images/modules/site/copilot/react/features-river-3.webp"
                      alt="GitHub Copilot adding a summary to a pull request"
                      width="708"
                      height="472"
                    />
                  </River.Visual>
                  <River.Content>
                    <Heading as="h3">
                      <Label className="Label--inRiver">Copilot Enterprise</Label>
                      Pull requests that tell a story.
                    </Heading>
                    <Text as="p" variant="muted" className="River-text">
                      GitHub Copilot keeps track of your work, suggests descriptions, and helps reviewers reason about
                      your changes.
                    </Text>
                  </River.Content>
                </River>
              </AnimationProvider>
            </Grid.Column>

            {/* Spacer */}
            <Box paddingBlockStart={{narrow: 96, regular: 128}} aria-hidden={true} />

            <Grid.Column span={12}>
              <Bento className="Bento Bento--raised">
                <Bento.Item
                  columnSpan={12}
                  rowSpan={{xsmall: 8, small: 6, medium: 5, large: 5}}
                  visualAsBackground
                  className="Features-bento-5"
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}} className="Features-bento-5-content">
                    <Bento.Heading as="h3" size="3" weight="semibold">
                      <Label className="Label--inBento">Copilot Enterprise</Label>
                      Prefer bespoke? Fine-tune a private copilot for the precision enterprises demand.
                    </Bento.Heading>
                    <Link href="https://github.co/copilot-custom-models" size="large" variant="default">
                      Join the waitlist
                    </Link>
                  </Bento.Content>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 7}}
                  rowSpan={{xsmall: 5, medium: 5, large: 5, xlarge: 6}}
                  visualAsBackground
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}} verticalAlign="start">
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Ask for assistance right in your terminal.
                    </Bento.Heading>
                    <Link
                      href="https://docs.github.com/en/copilot/github-copilot-in-the-cli"
                      size="large"
                      variant="default"
                    >
                      Try Copilot in the CLI
                    </Link>
                  </Bento.Content>
                  <Bento.Visual position="0% 50%">
                    <Image
                      src="/images/modules/site/copilot/react/features-bento-1.webp"
                      alt="Screenshot of GitHub Copilot CLI in a terminal"
                      width="724"
                      height="620"
                      className="Features-bento-1-visual"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 5}}
                  rowSpan={{xsmall: 5, medium: 5, large: 5, xlarge: 6}}
                  className="Bento-item"
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}} horizontalAlign="center">
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Keep flying with your favorite editor.
                    </Bento.Heading>
                  </Bento.Content>
                  <Bento.Visual
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    fillMedia={false}
                    className="Features-editorContainer"
                  >
                    <Button
                      as="a"
                      href="https://marketplace.visualstudio.com/items?itemName=GitHub.copilot"
                      hasArrow={false}
                      className="Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/react/features-bento-2-vscode.svg"
                        alt=""
                        width="90"
                        height="90"
                      />
                      <Text as="div" size="200" weight="normal">
                        VS Code
                      </Text>
                    </Button>

                    <Button
                      as="a"
                      href="https://docs.github.com/copilot/getting-started-with-github-copilot?tool=vimneovim"
                      hasArrow={false}
                      className="Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/react/features-bento-2-neovim.svg"
                        alt=""
                        width="90"
                        height="90"
                      />
                      <Text as="div" size="200" weight="normal">
                        Neovim
                      </Text>
                    </Button>

                    <Button
                      as="a"
                      href="https://marketplace.visualstudio.com/items?itemName=GitHub.copilotvs"
                      hasArrow={false}
                      className="Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/react/features-bento-2-visualstudio.svg"
                        alt=""
                        width="90"
                        height="90"
                      />
                      <Text as="div" size="200" weight="normal">
                        Visual Studio
                      </Text>
                    </Button>

                    <Button
                      as="a"
                      href="https://plugins.jetbrains.com/plugin/17718-github-copilot"
                      hasArrow={false}
                      className="Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/react/features-bento-2-jetbrains.svg"
                        alt=""
                        width="90"
                        height="90"
                      />
                      <Text as="div" size="200" weight="normal">
                        JetBrains IDEs
                      </Text>
                    </Button>
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 5, large: 5, xlarge: 5}}
                  rowSpan={{xsmall: 4, small: 4, medium: 4, xlarge: 5}}
                  className="Bento-item"
                >
                  <Bento.Content
                    horizontalAlign={{xsmall: 'center', large: 'start'}}
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    leadingVisual={<DeviceMobileIcon />}
                    className="Features-mobile"
                  >
                    <Bento.Heading as="h3" size="4" weight="semibold" className="Features-mobileText">
                      Coming soon to <br className="breakWhenNarrow" /> GitHub Mobile.
                    </Bento.Heading>
                  </Bento.Content>
                  <Bento.Visual
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    fillMedia={false}
                    horizontalAlign="center"
                    verticalAlign="end"
                    style={{columnGap: '24px', flexWrap: 'wrap'}}
                  >
                    <a href="https://play.google.com/store/apps/details?id=com.github.android">
                      <Image
                        src="/images/modules/site/copilot/react/features-bento-4-google.png"
                        alt="Google Play Store logo"
                        width="180"
                        height="53"
                        style={{display: 'block'}}
                      />
                    </a>
                    <a href="https://apps.apple.com/app/github/id1477376905?ls=1">
                      <Image
                        src="/images/modules/site/copilot/react/features-bento-4-apple.png"
                        alt="Apple App Store logo"
                        width="179"
                        height="53"
                        style={{display: 'block'}}
                      />
                    </a>
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 7, large: 7, xlarge: 7}}
                  rowSpan={{xsmall: 4, small: 3, medium: 4, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Visual position="25% 0%">
                    <Image
                      src="/images/modules/site/copilot/react/features-bento-3.webp"
                      alt="A phone showing GitHub Copilot in GitHub Mobile"
                      width="724"
                      height="560"
                    />
                  </Bento.Visual>
                </Bento.Item>
              </Bento>
            </Grid.Column>
          </Grid>
        </section>

        <section id="pricing" className="Section Section--pricing" ref={sectionPricingRef}>
          <Grid className="Section-container--centerUntilMedium Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="SectionIntro">
                <SectionIntro.Label size="large" className="Label--section">
                  Pricing
                </SectionIntro.Label>
                <SectionIntro.Heading size="1" weight="bold">
                  Take flight with GitHub Copilot.
                </SectionIntro.Heading>
                <SectionIntro.Description className="SectionIntro-description--wide">
                  Organizations and developers all over the world use GitHub Copilot to code faster, drive impact, and
                  focus on doing what matters most: building great software.
                </SectionIntro.Description>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <Heading as="h3" size="5" className="Pricing-groupTitle">
                For organizations
              </Heading>

              <Grid className="Pricing">
                <Grid.Column span={12} className="Pricing-item has-BlurredBg has-GradientBorder">
                  <Stack
                    direction={{narrow: 'vertical', regular: 'horizontal'}}
                    gap="none"
                    padding="none"
                    className="Pricing--business"
                  >
                    {/* Copilot */}
                    <Stack padding="none" gap={24} style={{flex: '1'}}>
                      <Box>
                        <Text size="100" weight="semibold" className="Label--pricing">
                          Recommended
                        </Text>
                        <Box marginBlockStart={8} marginBlockEnd={16}>
                          <Heading as="h4" size="5">
                            Copilot Business
                          </Heading>
                        </Box>
                        <Text as="p" size="200" weight="normal" variant="muted" className="Pricing-description--org">
                          Copilot in the coding environment.
                        </Text>
                      </Box>

                      <Stack direction="horizontal" gap={12} padding="none" className="Pricing-price">
                        <Text size="500" weight="normal" style={{lineHeight: 1.4}} className="sansSerifAlt">
                          $
                        </Text>
                        <Text weight="normal" className="Pricing-nr sansSerifAlt">
                          19
                        </Text>
                        <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                          per user / month
                        </Text>
                      </Stack>

                      <hr className="Pricing-divider" />

                      <UnorderedList variant="checked" className="Pricing-list">
                        <UnorderedList.Item>Code completions</UnorderedList.Item>
                        <UnorderedList.Item>
                          Chat in IDE <sup>1</sup> and Mobile <sup>2</sup>
                        </UnorderedList.Item>
                        <UnorderedList.Item>
                          CLI assistance <sup>3</sup>
                        </UnorderedList.Item>
                        <UnorderedList.Item>Security vulnerability filter</UnorderedList.Item>
                        <UnorderedList.Item>Code referencing</UnorderedList.Item>
                        <UnorderedList.Item>Public code filter</UnorderedList.Item>
                        <UnorderedList.Item>IP indemnity</UnorderedList.Item>
                        <UnorderedList.Item>Enterprise-grade security, safety, and privacy</UnorderedList.Item>
                      </UnorderedList>

                      <Stack direction={{narrow: 'vertical', wide: 'horizontal'}} gap={12} padding="none">
                        <Button
                          as="a"
                          href={copilotForBusinessSignupPath}
                          block
                          variant="primary"
                          className="Button--narrowGap"
                        >
                          Buy now
                        </Button>
                        <Button
                          as="a"
                          href="https://github.com/enterprise/contact"
                          block
                          className="Button--narrowGap Button--onColoredBg"
                        >
                          Contact sales
                        </Button>
                      </Stack>
                    </Stack>

                    <hr className="Pricing-divider Pricing-divider--business" />

                    {/* Copilot Enterprise */}
                    <Stack padding="none" gap={24} style={{flex: '1'}}>
                      <Box>
                        <Text size="100" weight="semibold" className="Label--pricing">
                          Available Feb 2024
                        </Text>
                        <Box marginBlockStart={8} marginBlockEnd={16}>
                          <Heading as="h4" size="5">
                            Copilot Enterprise
                          </Heading>
                        </Box>
                        <Text as="p" size="200" weight="normal" variant="muted" className="Pricing-description--org">
                          Copilot personalized to your organization throughout the software development lifecycle.
                          Requires GitHub Enterprise Cloud.
                        </Text>
                      </Box>

                      <Stack direction="horizontal" gap={12} padding="none" className="Pricing-price">
                        <Text size="500" weight="normal" className="sansSerifAlt" style={{lineHeight: 1.4}}>
                          $
                        </Text>
                        <Text weight="normal" className="Pricing-nr sansSerifAlt">
                          39
                        </Text>
                        <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                          per user / month
                        </Text>
                      </Stack>

                      <hr className="Pricing-divider" />

                      <Box style={{flex: '1'}}>
                        <Box className="Pricing-plus">
                          <ArrowLeftIcon size={16} />
                          <Text size="200" weight="normal" className="Pricing-plusText">
                            Everything in Copilot, plus:
                          </Text>
                        </Box>

                        <UnorderedList variant="checked" className="Pricing-list">
                          <UnorderedList.Item>Chat personalized to your codebase</UnorderedList.Item>
                          <UnorderedList.Item>Documentation search and summaries</UnorderedList.Item>
                          <UnorderedList.Item>Pull request summaries</UnorderedList.Item>
                          <UnorderedList.Item>Code review skills</UnorderedList.Item>
                          <UnorderedList.Item>
                            Fine-tuned models <sup>4</sup>
                          </UnorderedList.Item>
                        </UnorderedList>
                      </Box>
                      <Button
                        as="a"
                        href="https://github.co/copilot-enterprise"
                        variant="primary"
                        className="Button--narrowGap"
                      >
                        Join waitlist
                      </Button>
                    </Stack>
                  </Stack>
                </Grid.Column>
              </Grid>
            </Grid.Column>

            <Grid.Column span={12}>
              <Heading as="h3" size="5" className="Pricing-groupTitle Pricing-groupTitle--individuals">
                For individuals
              </Heading>

              <Grid className="Pricing">
                <Grid.Column
                  span={12}
                  className="Pricing-item has-BlurredBg has-GradientBorder"
                  style={{gap: '16px', backgroundColor: 'rgba(22, 27, 34, 0.85)'}}
                >
                  <Heading as="h4" size="5">
                    Copilot Individual
                  </Heading>

                  <Stack
                    direction={{narrow: 'vertical', regular: 'horizontal'}}
                    gap={24}
                    padding="none"
                    className="Pricing--multiColumn"
                  >
                    <Box style={{flex: '1'}}>
                      <Box marginBlockEnd={32}>
                        <Text as="p" size="200" weight="normal" variant="muted">
                          Code completions, Chat, and more for indie developers and freelancers.
                        </Text>
                      </Box>

                      <Stack direction="horizontal" gap={12} padding="none">
                        <Text size="500" weight="normal" className="sansSerifAlt" style={{lineHeight: 1.4}}>
                          $
                        </Text>
                        <Text weight="normal" className="Pricing-nr sansSerifAlt">
                          10
                        </Text>
                        <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                          per month / <br className="breakWhenWide" />
                          $100 per year
                        </Text>
                      </Stack>
                    </Box>

                    <Box style={{flex: '1'}}>
                      <Button as="a" href={copilotSignupPath} block variant="primary" className="Button--narrowGap">
                        Start a free trial
                      </Button>
                      <Text
                        as="p"
                        size="100"
                        weight="normal"
                        variant="muted"
                        style={{marginTop: 'var(--base-size-32)'}}
                      >
                        Free for verified students, teachers, and maintainers of popular open source projects.
                      </Text>
                    </Box>
                  </Stack>
                </Grid.Column>
              </Grid>
            </Grid.Column>

            {/* 3 column pricing plan (might be used in an A/B test) */}
            {/*
            <Grid.Column span={12}>
              <Grid className="Pricing">
                <Grid.Column span={{xsmall: 12, large: 4}} className="Pricing-item has-BlurredBg">
                  <Box>
                    <Heading as="h3" size="6" style={{marginBottom: 'var(--base-size-16)'}}>
                      Individual
                    </Heading>
                    <Text as="p" size="200" weight="normal" variant="muted">
                      For indie developers and freelancers. Free for students, educators, and open source maintainers.
                    </Text>
                  </Box>

                  <Stack direction="horizontal" gap={12} padding="none">
                    <Text size="400" weight="normal" style={{lineHeight: 1.75}}>
                      $
                    </Text>
                    <Text size="700" weight="normal">
                      10
                    </Text>
                    <Text size="100" weight="normal" variant="muted" style={{lineHeight: 1.75}}>
                      per month /<br />
                      $100 per year
                    </Text>
                  </Stack>

                  <hr className="Pricing-divider" />

                  <UnorderedList variant="checked" className="Pricing-list">
                    <UnorderedList.Item>Plugs into the editor</UnorderedList.Item>
                    <UnorderedList.Item>Turns natural language prompts into code</UnorderedList.Item>
                    <UnorderedList.Item>Offers multi-line function suggestions</UnorderedList.Item>
                    <UnorderedList.Item>Speeds up test generation</UnorderedList.Item>
                    <UnorderedList.Item>Filters out common vulnerable coding patterns</UnorderedList.Item>
                    <UnorderedList.Item>Blocks suggestions matching public code</UnorderedList.Item>
                    <UnorderedList.Item>Copilot Chat beta</UnorderedList.Item>
                    <UnorderedList.Item>CLI assistance beta</UnorderedList.Item>
                  </UnorderedList>

                  <Text as="p" size="200" weight="normal" variant="muted">
                    Copilot Chat and CLI assistance generally available February 2024.
                  </Text>

                  <Button as="a" href={copilotSignupPath} variant="primary">
                    Start a free trial
                  </Button>
                </Grid.Column>

                <Grid.Column span={{xsmall: 12, large: 4}} className="Pricing-item has-BlurredBg">
                  <Box>
                    <Heading as="h3" size="6" style={{marginBottom: 'var(--base-size-16)'}}>
                      Business
                    </Heading>
                    <Text as="p" size="200" weight="normal" variant="muted">
                      Empower development teams to code smarter, faster, and happier with AI in the IDE.
                    </Text>
                  </Box>

                  <Stack direction="horizontal" gap={12} padding="none" className="Pricing-price">
                    <Text size="400" weight="normal" style={{lineHeight: 1.75}}>
                      $
                    </Text>
                    <Text size="700" weight="normal">
                      19
                    </Text>
                    <Text size="100" weight="normal" variant="muted" style={{lineHeight: 1.75}}>
                      per user / month
                    </Text>
                  </Stack>

                  <hr className="Pricing-divider" />

                  <Box className="Pricing-plus">
                    <ArrowLeftIcon size={16} />
                    <Text size="200" weight="normal" className="Pricing-plusText">
                      Everything in Individual, plus…
                    </Text>
                  </Box>

                  <UnorderedList variant="checked" className="Pricing-list">
                    <UnorderedList.Item>License management via console and API</UnorderedList.Item>
                    <UnorderedList.Item>Enterprise-grade privacy and policy management</UnorderedList.Item>
                    <UnorderedList.Item>SAML single sign-on</UnorderedList.Item>
                    <UnorderedList.Item>Corporate proxy support</UnorderedList.Item>
                    <UnorderedList.Item>Microsoft Copilot Copyright Commitment indemnity</UnorderedList.Item>
                  </UnorderedList>

                  <Stack direction="vertical" gap={12} padding="none">
                    <Button as="a" href="https://github.com/enterprise/contact" className="Button--onColoredBg">
                      Contact sales
                    </Button>
                    <Button as="a" href={copilotForBusinessSignupPath} variant="primary">
                      Buy now
                    </Button>
                  </Stack>
                </Grid.Column>

                <Grid.Column span={{xsmall: 12, large: 4}} className="Pricing-item has-BlurredBg">
                  <Box>
                    <Heading as="h3" size="6" style={{marginBottom: 'var(--base-size-16)'}}>
                      Enterprise
                    </Heading>
                    <Text as="p" size="200" weight="normal" variant="muted">
                      Infuse AI into GitHub to accelerate every step of the software development lifecycle.
                    </Text>
                  </Box>

                  <Stack direction="horizontal" gap={12} padding="none" className="Pricing-price">
                    <Text size="400" weight="normal" style={{lineHeight: 1.75}}>
                      $
                    </Text>
                    <Text size="700" weight="normal">
                      39
                    </Text>
                    <Text size="100" weight="normal" variant="muted" style={{lineHeight: 1.75}}>
                      per user / month
                    </Text>
                  </Stack>

                  <hr className="Pricing-divider" />

                  <Box className="Pricing-plus">
                    <ArrowLeftIcon size={16} />
                    <Text size="200" weight="normal" className="Pricing-plusText">
                      Everything in Business, plus…
                    </Text>
                  </Box>

                  <UnorderedList variant="checked" className="Pricing-list">
                    <UnorderedList.Item>Copilot in the GitHub Platform</UnorderedList.Item>
                    <UnorderedList.Item>Conversational documentation search</UnorderedList.Item>
                    <UnorderedList.Item>Summarization skills for pull requests</UnorderedList.Item>
                    <UnorderedList.Item>Code explanations and diffs analysis</UnorderedList.Item>
                  </UnorderedList>

                  <Text as="p" size="200" weight="normal" variant="muted">
                    Available February 2024.Requires GitHub Enterprise Cloud.
                  </Text>

                  <Button as="a" href="https://www.github.com/features/preview/copilot-enterprise" variant="primary">
                    Join waitlist
                  </Button>
                </Grid.Column>
              </Grid>
            </Grid.Column>
            */}
          </Grid>

          {/* Spacer */}
          <Box paddingBlockStart={32} aria-hidden={true} />

          <div className="Pricing-bgCover">
            <Image className="Pricing-bgCoverImage" src="/images/modules/site/copilot/react/pricing-cover.svg" alt="" />
          </div>
        </section>

        <section id="resources" className="Section Section--compact">
          <Grid className="Section-container--centerUntilMedium Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="SectionIntro">
                <SectionIntro.Heading size="3">Get the most out of GitHub Copilot.</SectionIntro.Heading>
              </SectionIntro>

              <Stack
                direction={{narrow: 'vertical', regular: 'horizontal', wide: 'horizontal'}}
                gap="normal"
                padding="none"
              >
                <div className="Resources-card">
                  <Card ctaText="Explore GitHub Expert Services" href="https://github.com/services/">
                    <Card.Icon icon={PeopleIcon} color="purple" hasBackground />
                    <Card.Heading>Hands-on consulting, guided workshops, and training.</Card.Heading>
                    <Card.Description>
                      Insights, best practices, and knowledge to help you adopt GitHub quickly and efficiently.
                    </Card.Description>
                  </Card>
                </div>

                <div className="Resources-card">
                  <Card ctaText="Read customer stories" href="https://github.com/customer-stories">
                    <Card.Icon icon={BookIcon} color="purple" hasBackground />
                    <Card.Heading>Meet the companies who build with GitHub.</Card.Heading>
                    <Card.Description>
                      Leading organizations choose GitHub to plan, build, secure and ship software.
                    </Card.Description>
                  </Card>
                </div>

                <div className="Resources-card">
                  <Card ctaText="Read blog" href="https://github.blog/">
                    <Card.Icon icon={BookIcon} color="purple" hasBackground />
                    <Card.Heading>Keep up with the latest on GitHub and trends in AI.</Card.Heading>
                    <Card.Description>
                      Check out the GitHub blog for tips, technical guides, best practices, and more.
                    </Card.Description>
                  </Card>
                </div>
              </Stack>
            </Grid.Column>
          </Grid>
        </section>

        <section id="faq" className="Section Section--level-1">
          <Grid>
            <Grid.Column span={12} className="FAQs">
              <FAQGroup>
                <FAQGroup.Heading>
                  Frequently asked <br /> questions.
                </FAQGroup.Heading>

                <FAQ>
                  <FAQ.Heading>General</FAQ.Heading>
                  <FAQ.Item>
                    <FAQ.Question>What is GitHub Copilot?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub Copilot transforms the developer experience. Backed by the leaders in AI, Copilot
                        provides contextualized assistance throughout the software development lifecycle, from code
                        completions in the IDE to code explanations in GitHub and more. With Copilot elevating their
                        workflow, developers can focus on more: value, innovation, and happiness.
                      </p>
                      <p>
                        Copilot enables developers to focus more energy on problem solving and collaboration and spend
                        less effort on the mundane and boilerplate. That’s why developers who use Copilot report up to{' '}
                        <a href="https://github.blog/2022-09-07-research-quantifying-github-copilots-impact-on-developer-productivity-and-happiness/">
                          75% higher satisfaction with their jobs than those who don’t and are up to 55% more productive
                          at writing code
                        </a>{' '}
                        without sacrifice to quality, which all adds up to engaged developers shipping great software
                        faster.
                      </p>
                      <p>
                        Copilot integrates with leading editors, including Visual Studio Code, Visual Studio, JetBrains
                        IDEs, and Neovim, and, unlike other AI coding assistants, is natively built into GitHub. Growing
                        to millions of individual users and tens of thousands of business customers, Copilot is the
                        world’s most widely adopted AI developer tool and the competitive advantage developers ask for
                        by name.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      What are the differences between the GitHub Copilot Business, GitHub Copilot Enterprise, and
                      GitHub Copilot Individual plans?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub Copilot has offerings for organizations and an offering for individual developers. The
                        primary differences between the organization offerings and the individual offering are license
                        management, policy management, and IP indemnity.
                      </p>
                      <p>
                        Organizations can choose between GitHub Copilot Business and GitHub Copilot Enterprise, which is
                        coming in February 2024. GitHub Copilot Business primarily features GitHub Copilot in the coding
                        environment - that is the IDE and CLI. In early-2024, it will also include GitHub Copilot in
                        GitHub Mobile. GitHub Copilot Enterprise includes everything in GitHub Copilot Business and adds
                        an additional layer of personalization for organizations as well as GitHub Copilot integrated
                        into GitHub as a chat interface to allow developers to converse about their codebase and action
                        buttons throughout the platform. GitHub Copilot Enterprise can index an organization’s codebase
                        for a deeper understanding of the customer’s knowledge for more tailored suggestions and will
                        offer customers access to GitHub Copilot Customization to fine-tune custom, private models for
                        code completion.
                      </p>
                      <p>
                        GitHub Copilot Individual is designed for individual developers, freelancers, students,
                        educators, and open source maintainers. The plan includes all the features of GitHub Copilot
                        Business except organizational license management, policy management, and IP indemnity.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>What languages, IDEs, and platforms does GitHub Copilot support?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub Copilot is trained on all languages that appear in public repositories. For each
                        language, the quality of suggestions you receive may depend on the volume and diversity of
                        training data for that language. For example, JavaScript is well-represented in public
                        repositories and is one of GitHub Copilot’s best supported languages. Languages with less
                        representation in public repositories may produce fewer or less robust suggestions.
                      </p>
                      <p>
                        GitHub Copilot is available as an extension in Visual Studio Code, Visual Studio, Vim, Neovim,
                        the JetBrains suite of IDEs, and Azure Data Studio. GitHub Copilot is also supported in
                        terminals through GitHub CLI. GitHub Copilot will soon be natively integrated into GitHub web
                        and mobile.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>What data has GitHub Copilot been trained on?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub Copilot is powered by generative AI models developed by GitHub, OpenAI, and Microsoft. It
                        has been trained on natural language text and source code from publicly available sources,
                        including code in public repositories on GitHub.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Does GitHub Copilot “copy/paste”?</FAQ.Question>
                    <FAQ.Answer>
                      <p>No, GitHub Copilot generates suggestions using probabilistic reasoning.</p>
                      <ul>
                        <li>
                          When thinking about intellectual property and open source issues, it is critical to understand
                          how GitHub Copilot really works. The AI models that create Copilot’s suggestions may be
                          trained on public code, but do not contain any code. When they generate a suggestion, they are
                          not “copying and pasting” from any codebase.
                        </li>
                        <li>
                          To generate a suggestion, Copilot begins by examining the code in your editor—focusing on the
                          lines just before and after your cursor, but also information in other files open in your
                          editor. That information is sent to Copilot’s model, to make a probabilistic determination of
                          what is likely to come next and generate suggestions.
                        </li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                </FAQ>

                <FAQ>
                  <FAQ.Heading>Privacy</FAQ.Heading>
                  <FAQ.Item>
                    <FAQ.Question>
                      Where can I learn about GitHub Copilot privacy, security, and responsible AI policies?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        You can visit the{' '}
                        <a href="https://resources.github.com/copilot-trust-center/">GitHub Copilot Trust Center</a> for
                        more information on these topics.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>What personal data does GitHub Copilot collect?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub Copilot personal data from three categories of data: user engagement data, prompts and
                        suggestions.
                      </p>
                      <p>
                        <strong>User engagement data</strong>
                        <br />
                        User engagement data is usage information about events generated when interacting with a code
                        editor. These events include user edit actions (for example completions accepted and dismissed),
                        error messages, and general usage data to identify user metrics such as latency and feature
                        engagement. This information may include personal data, such as pseudonymous identifiers.
                      </p>
                      <p>
                        <strong>Prompts</strong>
                        <br />A prompt is the collection of code and supporting contextual information that the GitHub
                        Copilot extension sends to GitHub to generate suggestions. The extension sends a prompt when a
                        user working on a file, pauses typing, or uses a designated keyboard shortcut to request a
                        suggestion.
                      </p>
                      <p>
                        <strong>Suggestions</strong>
                        <br />A suggestion is one or more lines of proposed code and other output returned to the
                        Copilot extension after a prompt is received and processed by the AI models that power Copilot.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Does GitHub Copilot use Prompts or Suggestions to train AI models?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        No, GitHub Copilot does not use Prompts or Suggestions to train AI models. These inputs are not
                        retained or utilized in the training process of AI models for GitHub Copilot.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>How else is personal data in GitHub Copilot for Business used?</FAQ.Question>
                    <FAQ.Answer>
                      <p>GitHub processes user engagement data as a process to provide the service, which includes:</p>
                      <ul>
                        <li>
                          To deliver functional capabilities as licensed, configured, and used by Customer and its
                          users, including providing personalized user experiences;
                        </li>
                        <li>Troubleshooting (preventing, detecting, and repairing problems); and</li>
                        <li>
                          Keeping Products up to date and performant, and enhancing user productivity, reliability,
                          efficacy, quality, and security.
                        </li>
                      </ul>
                      <p>
                        Per Customer instruction in the GitHub DPA, GitHub processes UED as a data controller for the
                        purposes of:
                      </p>
                      <ul>
                        <li>Billing and account management;</li>
                        <li>Compensation such as calculating employee commissions and partner incentives;</li>
                        <li>
                          Aggregated internal reporting and business modeling, such as forecasting, revenue, capacity
                          planning, and product strategy; and
                        </li>
                        <li>Aggregated financial reporting.</li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>How long does GitHub Copilot retain personal data?</FAQ.Question>
                    <FAQ.Answer>
                      <p>Prompts</p>
                      <ul>
                        <li>
                          Prompts are discarded once a suggestion is returned. Copilot Individual users may opt to allow
                          GitHub to retain prompts.
                        </li>
                      </ul>
                      <p>Suggestions</p>
                      <ul>
                        <li>
                          Suggestions are not retained by GitHub. Copilot Individual users may opt to allow GitHub to
                          retain prompts.
                        </li>
                      </ul>
                      <p>User engagement data</p>
                      <ul>
                        <li>User engagement data is retained by GitHub for 24 months.</li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>How are the transmitted Prompts and Suggestions protected?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        We know that user edit actions, source code snippets, and URLs of repositories and file paths
                        are sensitive data. Consequently, several measures of protection are applied, including:
                      </p>
                      <ul>
                        <li>The transmitted data is encrypted in transit and at rest</li>
                        <li>
                          Access is strictly controlled. The data can only be accessed by (1) named GitHub personnel
                          working on the GitHub Copilot team or on the GitHub platform health team, (2) Microsoft
                          personnel working on or with the GitHub Copilot team, and (3) OpenAI personnel who work on
                          GitHub Copilot
                        </li>
                        <li>
                          Role-based access controls and multi-factor authentication are required for personnel
                          accessing code snippet data
                        </li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>How can users of GitHub Copilot control use of their data?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        User Engagement Data (which includes pseudonymous identifiers and general usage data), is
                        required for the use of GitHub Copilot and will continue to be collected, processed, and shared
                        with Microsoft as you use GitHub Copilot.
                      </p>
                      <p>Prompts and Suggestions for GitHub Copilot Business users are not retained by GitHub.</p>
                      <p>
                        Users of GitHub Copilot Individual can choose whether Prompts and Suggestions are retained by
                        GitHub and further processed and shared with Microsoft by{' '}
                        <a href="https://github.com/settings/copilot">adjusting user settings</a>. Users of GitHub
                        Copilot Individual can request deletion of Prompts and Suggestions associated with their GitHub
                        identity by <a href="https://support.github.com/request">filling out a support ticket</a>.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Will my code be shared with other users?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        No. We follow responsible practices in accordance with our{' '}
                        <a href="https://docs.github.com/en/site-policy/privacy-policies/github-privacy-statement">
                          Privacy Statement
                        </a>{' '}
                        to ensure that your code snippets will not be used as suggested code for other users of GitHub
                        Copilot.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Does GitHub Copilot ever output personal data?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Because the model powering GitHub Copilot was trained on publicly available code, its training
                        set included personal data that was included in that code. From our internal testing, we found
                        it to be very rare that GitHub Copilot suggestions included personal data verbatim from the
                        training set.
                      </p>
                      <p>
                        In some cases, the model will suggest what appears to be personal data – email addresses, phone
                        numbers, etc. – but those suggestions are actually fictitious information synthesized from
                        patterns in training data and therefore do not relate to any particular individual. For example,
                        when one of our engineers prompted GitHub Copilot with, “My name is Mona and my birthdate is,”
                        GitHub Copilot suggested a random, fictitious date of “December 12,” which is not Mona’s actual
                        birthdate.
                      </p>
                      <p>
                        We have also implemented a filter that blocks emails when shown in standard formats, but it’s
                        still possible to get the model to suggest this sort of content if you try hard enough. We will
                        keep improving the filter system to be more intelligent to detect and remove more personal data
                        from the GitHub Copilot suggestions.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      What if I’m accused of copyright infringement based on using a GitHub Copilot suggestion?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub will defend you as provided in the{' '}
                        <a href="https://github.com/customer-terms/github-copilot-product-specific-terms">
                          GitHub Copilot Product Specific Terms
                        </a>
                        .
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                </FAQ>

                <FAQ>
                  <FAQ.Heading>Responsible AI</FAQ.Heading>
                  <FAQ.Item>
                    <FAQ.Question>
                      What are the intellectual property considerations when using GitHub Copilot?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        The primary IP considerations for GitHub Copilot relate to copyright. The model that powers
                        Copilot is trained on a broad collection of publicly accessible code, which may include
                        copyrighted code, and Copilot’s suggestions (in rare instances) may resemble the code its model
                        was trained on. Here’s some basic information you should know about these considerations:
                      </p>
                      <p>
                        Copyright law permits the use of copyrighted works to train AI models: Countries around the
                        world have provisions in their copyright laws that enable machines to learn, understand, extract
                        patterns, and facts from copyrighted materials, including software code. By example, the
                        European Union, Japan, and Singapore, have express provisions permitting machine learning to
                        develop AI models. Other countries including Canada, India, and the United States also permit
                        such training under their fair use/fair dealing provisions. GitHub Copilot’s AI model was
                        trained with the use of code from GitHub’s public repositories—which are publicly accessible and
                        within the scope of permissible copyright use.
                      </p>
                      <p>
                        What about copyright risk in suggestions? In rare instances (less than 1% based on GitHub’s
                        research), suggestions from GitHub may match examples of code used to train GitHub’s AI model.
                        Again, Copilot does not “look up” or “copy and paste” code, but is instead using context from a
                        user’s code editor to synthesize and generate a suggestion. Our experience shows that matching
                        suggestions are most likely to occur in two situations: (i) when there is little or no context
                        in the code editor for Copilot’s model to synthesize, or (ii) when a matching suggestion
                        represents a common approach or method. If a suggestion matches existing copyrighted code, there
                        is risk that using that suggestion could trigger claims of copyright infringement, which would
                        depend on the amount and nature of code used, and the context of how the code is used. In many
                        ways, this is the same risk that arises when using any code that a developer does not originate,
                        such as copying code from an online source, or reusing code from a library. That is why
                        responsible organizations and developers recommend that users employ code scanning policies to
                        identify and evaluate potential matching code.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Does GitHub Copilot include a filtering mechanism to mitigate risk?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Yes, GitHub Copilot does include an optional code referencing filter to detect and suppress
                        certain suggestions that match public code on GitHub.
                      </p>
                      <ul>
                        <li>
                          GitHub has created a duplication detection filter to detect and suppress GitHub Copilot
                          suggestions that contain code that includes snippets of at least 150 characters that match
                          public code on GitHub. This filter is enabled by the administrator for your enterprise and it
                          can apply for all organizations within your enterprise or the administrator can defer control
                          to individual organizations.
                        </li>
                        <li>
                          With the filter enabled, Copilot checks code suggestions with its surrounding code for matches
                          or near matches (ignoring whitespace) against public code on GitHub of about 150 characters.
                          If there is a match, the suggestion will not be shown to the user.
                        </li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      Does GitHub Copilot include features to make it easier for users to identify potentially relevant
                      open source licenses for matching suggestions?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Yes, GitHub Copilot is previewing a code-referencing feature to assist users to find and review
                        potentially relevant open source licenses. The code-referencing feature is currently in preview.
                      </p>
                      <ul>
                        <li>
                          If a suggestion matches publicly available code on GitHub, maintain consistency with your
                          organization’s open source policies and procedures. A prudent and responsible step should
                          include investigating available information to determine whether to use a suggestion.
                        </li>
                        <li>
                          GitHub Copilot’s code referencing feature identifies suggestions that contain exact [or near]
                          matches with public code. When a match is located, Copilot provides an alert that includes
                          links to repositories for any such matching code, along with any available information on
                          applicable software licenses, and logs this information. Copilot users can review this
                          information to determine whether the applicable suggestions are suitable for use, and whether
                          additional measures may be necessary to use them.
                        </li>
                        <li>
                          Copilot users can also use this feature as a tool for learning. Using the information provided
                          by the code referencing feature, a developer might find inspiration from other codebases,
                          discover documentation, and almost certainly gain confidence that this fragment is appropriate
                          to use in their project. They might take a dependency, provide attribution where appropriate,
                          or possibly even pursue another implementation strategy. By helping developers understand the
                          community context of their code in a manner that also preserves developer flow, we believe
                          Copilot will continue to deliver responsible innovation and true happiness at the keyboard.
                        </li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Who owns the suggestions provided by GitHub Copilot?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        We don’t determine whether a suggestion is capable of being owned, but we are clear that GitHub
                        does not claim ownership of a suggestion.
                      </p>
                      <ul>
                        <li>
                          Whether a suggestion generated by an AI model can be owned depends on many factors (e.g. the
                          intellectual property law in the relevant country, the length of the suggestion, the extent
                          that suggestion is considered ‘functional’ instead of expressive, etc).
                        </li>
                        <li>
                          If a suggestion is capable of being owned, our terms are clear: GitHub does not claim
                          ownership.
                        </li>
                        <li>
                          GitHub does not claim ownership of any suggestion. In certain cases, it is possible for
                          Copilot to produce similar suggestions to different users. For example, two unrelated users
                          both starting new files to code the quicksort algorithm in Java will likely get the same
                          suggestion. The possibility of providing similar suggestions to multiple users is a common
                          part of generative AI systems.
                        </li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Can GitHub Copilot introduce insecure code in its suggestions?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Public code may contain insecure coding patterns, bugs, or references to outdated APIs or
                        idioms. When GitHub Copilot synthesizes code suggestions based on this data, it can also
                        synthesize code that contains these undesirable patterns. This is something we care a lot about
                        at GitHub, and as of March 2023 we launched an{' '}
                        <a href="https://github.blog/2023-02-14-github-copilot-now-has-a-better-ai-model-and-new-capabilities/#:~:text=Filtering%20out%20security%20vulnerabilities%20with%20a%20new%20AI%20system">
                          AI-based vulnerability prevention system
                        </a>{' '}
                        that blocks insecure code patterns in real-time to make GitHub Copilot suggestions more secure.
                        Our model targets the most common vulnerable coding patterns, including{' '}
                        <a href="https://cwe.mitre.org/data/definitions/798.html">hardcoded credentials</a>,
                        <a href="https://cwe.mitre.org/data/definitions/89.html">SQL injections</a>, and{' '}
                        <a href="https://cwe.mitre.org/data/definitions/22.html">path injections</a>. Additionally, in
                        recent years we’ve provided tools such as GitHub Actions, Dependabot, and CodeQL to open source
                        projects to help improve code quality. Of course, you should always use GitHub Copilot together
                        with good testing and code review practices and security tools, as well as your own judgment.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      Is GitHub Copilot intended to fully automate code generation and replace developers?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        No. Copilot is a tool intended to make developers more efficient. It’s not intended to replace
                        developers, who should continue to apply the same sorts of safeguards and diligence they would
                        apply with regard to any third-party code of unknown origin.
                      </p>
                      <ul>
                        <li>
                          The product is called “Copilot” not “Autopilot” and it’s not intended to generate code without
                          oversight. You should use exactly the same sorts of safeguards and diligence with Copilot’s
                          suggestions as you would use with any third-party code.
                        </li>
                        <li>
                          Identifying best practices for use of third party code is beyond the scope of this section.
                          That said, whatever practices your organization currently uses – rigorous functionality
                          testing, code scanning, security testing, etc. – you should continue these policies with
                          Copilot’s suggestions. Moreover, you should make sure your code editor or editor does not
                          automatically compile or run generated code before you review it.
                        </li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Can GitHub Copilot users simply use suggestions without concern?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Not necessarily. GitHub Copilot users should align their use of Copilot with their respective
                        risk tolerances.
                      </p>
                      <ul>
                        <li>
                          As noted above, GitHub Copilot is not intended to replace developers, or their individual
                          skill and judgment, and is not intended to fully automate the process of code development. The
                          same risks that apply to the use of any third-party code apply to the use of Copilot’s
                          suggestions.
                        </li>
                        <li>
                          Depending on your particular use case, you should consider implementing the protections
                          discussed above. It is your responsibility to assess what is appropriate for the situation and
                          implement appropriate safeguards.
                        </li>
                        <li>
                          You’re entitled to IP indemnification from GitHub for the unmodified suggestions when
                          Copilot’s filtering is enabled. If you do elect to enable this feature, the copyright
                          responsibility is ours, not our customers. As part of our ongoing commitment to responsible
                          AI, GitHub and Microsoft extends our IP indemnity and protection support to our customers who
                          are empowering their teams with GitHub Copilot. Details{' '}
                          <a href="https://blogs.microsoft.com/on-the-issues/2023/09/07/copilot-copyright-commitment-ai-legal-concerns/">
                            here
                          </a>
                          .
                        </li>
                      </ul>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Does GitHub Copilot support accessibility features?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        We are conducting internal testing of GitHub Copilot’s ease of use by developers with
                        disabilities and working to ensure that GitHub Copilot is accessible to all developers. Please
                        feel free to share your feedback on GitHub Copilot accessibility in our{' '}
                        <a href="https://github.com/github-community/community/discussions/categories/copilot">
                          feedback forum
                        </a>
                        .
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Does GitHub Copilot produce offensive outputs?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        GitHub Copilot includes filters to block offensive language in the prompts and to avoid
                        synthesizing suggestions in sensitive contexts. We continue to work on improving the filter
                        system to more intelligently detect and remove offensive outputs. If you see offensive outputs,
                        please report them directly to{' '}
                        <a href="mailto:copilot-safety@github.com">copilot-safety@github.com</a> so that we can improve
                        our safeguards. GitHub takes this challenge very seriously and we are committed to addressing
                        it.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>Will GitHub Copilot work as well using languages other than English?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Given public sources are predominantly in English, GitHub Copilot will likely work less well in
                        scenarios where natural language prompts provided by the developer are not in English and/or are
                        grammatically incorrect. Therefore, non-English speakers might experience a lower quality of
                        service.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                </FAQ>

                <FAQ>
                  <FAQ.Heading>Upcoming features and offerings</FAQ.Heading>
                  <FAQ.Item>
                    <FAQ.Question>How does the GitHub Copilot Enterprise waitlist work?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        By joining the GitHub Copilot Enterprise waitlist, you are expressing your intent to be
                        contacted about the upcoming GitHub Copilot offering. You may also get an opportunity to test a
                        pre-release version of the product. If you are an administrator or member of a GitHub Enterprise
                        Cloud account, you will be able to nominate a specific enterprise to try GitHub Copilot
                        Enterprise in private beta.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>How does the GitHub Copilot Customization waitlist work?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        By joining the GitHub Copilot Customization waitlist, you are expressing your intent to be
                        contacted about the upcoming GitHub Copilot offering. You may also get an opportunity to test a
                        pre-release version of the product. If you are an administrator for an organization using GitHub
                        Enterprise Cloud or GitHub Copilot Business, you will be able to nominate a specific
                        organization to try GitHub Copilot Customization in private beta.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>How do I access the beta of GitHub Copilot Chat?</FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        Access to GitHub Copilot Chat requires an active GitHub Copilot license. You can learn how to
                        enable the feature{' '}
                        <a href="https://docs.github.com/en/copilot/github-copilot-chat/using-github-copilot-chat">
                          here
                        </a>
                        .
                      </p>
                      <p>
                        GitHub Copilot Chat in Visual Studio and Visual Studio code are in public beta and will be
                        generally available in December 2023. GitHub Copilot Business users are automatically granted
                        access as long as the enterprise or organization administrator has enabled the feature for the
                        organization. All GitHub Copilot Individual users are automatically eligible to use the
                        pre-release feature. Instructions to get set up can be found{' '}
                        <a href="https://docs.github.com/en/copilot/github-copilot-chat/using-github-copilot-chat">
                          here
                        </a>
                        .
                      </p>
                      <p>
                        GitHub Copilot Chat in JetBrains IDEs is in private beta. Users need to request access by
                        joining the{' '}
                        <a href="https://github.co/copilot-chat-jetbrains">
                          GitHub Copilot Chat in JetBrains IDEs waitlist
                        </a>
                        . Users will be notified by GitHub once they are invited to try the pre-release feature.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      I’m currently participating in or am on the waitlist for the GitHub Next technical preview for
                      GitHub Copilot for CLI. What should I do now that there is a public beta for GitHub Copilot in the
                      CLI?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        We recommend joining the public beta. You can find information on how to join{' '}
                        <a href="https://docs.github.com/en/copilot/github-copilot-in-the-cli">here</a>.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      I joined the public beta for GitHub Copilot in the CLI but the feature looks different than it did
                      during the GitHub Next technical preview. Why?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        The feedback we get from users during GitHub Next technical previews directly informs how we
                        refine those features before moving them into production. That’s one of the reasons technical
                        previews are so valuable, and it is not unusual for a feature to change when it moves from
                        GitHub Next to the GitHub product and engineering teams.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      GitHub announced a private beta of Github Copilot pull request summaries, but only through the new
                      GitHub Copilot Enterprise plan. What does that mean for users that are currently participating in
                      the GitHub Next technical preview for GitHub Copilot for Pull Requests or are on that waitlist?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        We have closed the waitlist for the GitHub Next technical preview. Users that have already been
                        admitted to the technical preview can continue to experiment with GitHub Copilot for Pull
                        Requests. GitHub Copilot features for pull requests are currently limited to the GitHub Copilot
                        Enterprise plan. If you are a developer or administrator that’s part of an enterprise, you can
                        join the GitHub Copilot Enterprise waitlist{' '}
                        <a href="https://github.co/copilot-enterprise">here</a>.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                  <FAQ.Item>
                    <FAQ.Question>
                      GitHub announced a private beta of Github Copilot documentation search, but only through the new
                      GitHub Copilot Enterprise plan. What does that mean for users that are currently participating in
                      the GitHub Next technical preview for GitHub Copilot for Docs or are on that waitlist?
                    </FAQ.Question>
                    <FAQ.Answer>
                      <p>
                        We have closed the waitlist for the GitHub Next technical preview. Users that have already been
                        admitted to the technical preview can continue to experiment with GitHub Copilot for Docs.
                        GitHub Copilot features for documentation are currently limited to the GitHub Copilot Enterprise
                        plan. If you are a developer or administrator that’s part of an enterprise, you can join the
                        GitHub Copilot Enterprise waitlist <a href="https://github.co/copilot-enterprise">here</a>.
                      </p>
                    </FAQ.Answer>
                  </FAQ.Item>
                </FAQ>
              </FAQGroup>
            </Grid.Column>
          </Grid>
        </section>

        <section id="footnotes" className="Section Section--level-1" style={{paddingTop: '0'}}>
          <Grid className="Grid--noRowGap">
            <Grid.Column span={12}>
              <Stack direction="horizontal" padding={12}>
                <Text variant="muted" size="100" style={{textAlign: 'right', minWidth: '2ch', marginRight: '60px'}}>
                  1
                </Text>
                <Text variant="muted" size="100">
                  Currently in preview. Chat in Visual Studio Code and Visual Studio will be generally available
                  late-2023. Chat in JetBrains IDEs to follow in 2024. Instructions to enable the preview can be found{' '}
                  <a
                    className="Link--inline"
                    href="https://docs.github.com/en/copilot/github-copilot-chat/using-github-copilot-chat"
                  >
                    here
                  </a>
                  .
                </Text>
              </Stack>

              <Stack direction="horizontal" padding={12}>
                <Text variant="muted" size="100" style={{textAlign: 'right', minWidth: '2ch', marginRight: '60px'}}>
                  2
                </Text>
                <Text variant="muted" size="100">
                  Chat in GitHub Mobile is coming soon.
                </Text>
              </Stack>

              <Stack direction="horizontal" padding={12}>
                <Text variant="muted" size="100" style={{textAlign: 'right', minWidth: '2ch', marginRight: '60px'}}>
                  3
                </Text>
                <Text variant="muted" size="100">
                  Currently in preview. Instructions to enable Copilot in the CLI in preview can be found{' '}
                  <a className="Link--inline" href="https://docs.github.com/en/copilot/github-copilot-in-the-cli">
                    here
                  </a>
                  .
                </Text>
              </Stack>

              <Stack direction="horizontal" padding={12}>
                <Text variant="muted" size="100" style={{textAlign: 'right', minWidth: '2ch', marginRight: '60px'}}>
                  4
                </Text>
                <Text variant="muted" size="100">
                  Currently in preview. Copilot Enterprise grants you access to customization for fine-tuned models
                  (available in 2024).
                </Text>
              </Stack>
            </Grid.Column>
          </Grid>
        </section>
      </ThemeProvider>
    </>
  )
}

try{ CopilotIndex.displayName ||= 'CopilotIndex' } catch {}